import { useEffect, useState, type ReactNode } from 'react';

import { Spinner } from './Controls';
import { Skeleton, SkeletonCards, SkeletonChart, SkeletonPage, SkeletonRecord, SkeletonRows, SkeletonText } from './Skeleton';

/**
 * The portal's loading state - one loading system.
 *
 * "Shell first, skeletons in the real geometry, words after a second, spinners
 * only inside controls." Every wait in the app goes through this component so
 * it follows the same clock wherever it happens:
 *
 *   0 - 300 ms  nothing new. A fast answer never flashes a skeleton.
 *   300 ms - 1s a skeleton in the shape of what is coming. Shimmer, not spin.
 *   1 - 5 s     words: what is loading, and for how long.
 *   > 5 s       a way out: Show what's loaded, Retry or Cancel. Never a dead end.
 *   > 10 s      a problem: name the dependency that is slow, keep the rest usable.
 *
 *   <Loading label="Loading the pipeline" shape="cards" />   a section or screen
 *   <Loading variant="page" />                               a routed screen
 *   <Loading variant="inline" label="Searching" />           beside text, in a control
 *
 * There is no big spinner any more. Before this, a routed screen waited behind
 * a 32px ring centred in an empty pane, sign-in behind another, and a dozen
 * panels wrote "Loading…" in a paragraph - three looks for one state.
 */

export type LoadingVariant = 'page' | 'block' | 'inline';

/** The geometry the skeleton takes - pick the one the real content has. */
export type LoadingShape = 'page' | 'rows' | 'cards' | 'record' | 'chart' | 'text' | 'canvas';

export interface LoadingProps {
  /**
   * What is being waited for, as a present participle: "Loading the pipeline",
   * "Searching", "Finding comparables". Shown from one second in, and the
   * accessible name throughout.
   */
  label?: string;
  variant?: LoadingVariant;
  /** Skeleton geometry. Defaults to `page` for a screen, `rows` for a section. */
  shape?: LoadingShape;
  /** Rows / cards / fields to draw, where the shape takes a count. */
  count?: number;
  /** The slow dependency to name past ten seconds - "URA REALIS", "OneMap". */
  dependency?: string;
  /** "Show what's loaded" / "Continue without": drop the wait, keep the page. */
  onShowLoaded?: () => void;
  /** Ask again. Without one, Retry reloads the page - still a way out. */
  onRetry?: () => void;
  onCancel?: () => void;
  /** Extra detail, shown with the words from one second in. */
  children?: ReactNode;
  className?: string;
}

/* The clock, in milliseconds. Exported so tests and the sign-in screen agree. */
export const LOADING_SKELETON_AFTER = 300;
export const LOADING_WORDS_AFTER = 1000;
export const LOADING_EXIT_AFTER = 5000;
export const LOADING_SLOW_AFTER = 10000;

/**
 * Milliseconds since mount, coarse until words appear (only the phase changes
 * matter) and then every 100 ms, for the "· 1.8 s" counter.
 */
export function useLoadingElapsed(active = true): number {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!active) {
      setElapsed(0);
      return;
    }

    const start = performance.now();
    let timer: number;

    const tick = () => {
      const now = performance.now() - start;

      setElapsed(now);
      timer = window.setTimeout(tick, now < LOADING_WORDS_AFTER ? Math.max(16, LOADING_WORDS_AFTER - now) : 100);
    };

    timer = window.setTimeout(tick, LOADING_SKELETON_AFTER);

    return () => window.clearTimeout(timer);
  }, [active]);

  return elapsed;
}

const seconds = (ms: number) => `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)} s`;

function SkeletonFor({ shape, count }: { shape: LoadingShape; count?: number }) {
  switch (shape) {
    case 'page':
      return <SkeletonPage />;
    case 'cards':
      return <SkeletonCards cards={count} />;
    case 'record':
      return <SkeletonRecord fields={count} />;
    case 'chart':
      return <SkeletonChart bars={count} />;
    case 'text':
      return <SkeletonText lines={count} />;
    case 'canvas':
      // A map or an image: one surface filling the box, no detail to fake.
      return <Skeleton shape="block" className="cw-skel-canvas" />;
    default:
      return <SkeletonRows rows={count} />;
  }
}

export function Loading({
  label = 'Loading',
  variant = 'block',
  shape,
  count,
  dependency,
  onShowLoaded,
  onRetry,
  onCancel,
  children,
  className
}: LoadingProps) {
  const inline = variant === 'inline';
  const elapsed = useLoadingElapsed(!inline);

  /*
   * Inline is the control case: the spinner lives inside the thing that was
   * pressed, at 14px, beside the present participle. It shows at once - the
   * press needs an answer - and never grows a skeleton.
   */
  if (inline) {
    return (
      <span className={['cw-loading', 'cw-loading--inline', className].filter(Boolean).join(' ')} role="status" aria-live="polite">
        <Spinner label={label} />
        {label && <span className="cw-loading__inline-label">{label}…</span>}
      </span>
    );
  }

  const showSkeleton = elapsed >= LOADING_SKELETON_AFTER;
  const showWords = elapsed >= LOADING_WORDS_AFTER;
  const showExit = elapsed >= LOADING_EXIT_AFTER;
  const slow = elapsed >= LOADING_SLOW_AFTER;

  const retry = onRetry ?? (() => window.location.reload());

  return (
    <div
      className={['cw-loading', `cw-loading--${variant}`, showSkeleton ? null : 'is-quiet', className].filter(Boolean).join(' ')}
      /*
       * `status` with a polite live region, and the label as its name from the
       * first frame: a screen reader hears "Loading the pipeline" even in the
       * first 300ms, when nothing is drawn.
       */
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label}
    >
      {showSkeleton && <SkeletonFor shape={shape ?? (variant === 'page' ? 'page' : 'rows')} count={count} />}

      {slow && (
        <div className="cw-loading__slow" role="alert">
          <Spinner label={label} />
          <span>
            <strong>{dependency ?? 'This'} is slow</strong> ({seconds(elapsed)}).{' '}
            {dependency
              ? 'What depends on it will fill in when it answers; everything else is ready.'
              : 'It is taking longer than usual; the rest of the portal is still usable.'}{' '}
            <span className="cw-loading__actions">
              {(onShowLoaded ?? onCancel) && (
                <button type="button" className="cw-loading__action" onClick={onShowLoaded ?? onCancel}>
                  Continue without
                </button>
              )}
              <button type="button" className="cw-loading__action" onClick={retry}>
                Retry
              </button>
            </span>
          </span>
        </div>
      )}

      {showWords && !slow && (
        <div className="cw-loading__status">
          <span className="cw-loading__words">
            <Spinner label={label} />
            <span>
              {label} · {seconds(elapsed)}
            </span>
          </span>

          {showExit && (
            <span className="cw-loading__actions">
              {onShowLoaded && (
                <button type="button" className="cw-loading__action" onClick={onShowLoaded}>
                  Show what's loaded
                </button>
              )}
              <button type="button" className="cw-loading__action" onClick={retry}>
                Retry
              </button>
              {onCancel && (
                <button type="button" className="cw-loading__action" onClick={onCancel}>
                  Cancel
                </button>
              )}
            </span>
          )}
        </div>
      )}

      {showWords && children && <div className="cw-loading__detail">{children}</div>}
    </div>
  );
}

export default Loading;
